import React, {Suspense} from 'react';
import Meta from "../components/common/Meta";
import MainDesign from "./MainDesign";
import AnimatedLink from "../components/ui/motion/AnimatedLink";
import SlideInWhenVisible from "../components/ui/motion/SlideInWhenVisible";
import Heading from "../components/common/header/Heading";
import {TraceService} from "../service/TraceService";
import {TraceType} from "../model/user/general/Trace";
import {motion} from "framer-motion";
import StaggerChildrenWhenVisible from '../components/ui/motion/StaggerChildrenWhenVisible';
import {Await, defer, useLoaderData} from "react-router-dom";
import {BlogService} from "../service/BlogService";
import LoadingPage from "./common/LoadingPage";
import {BlogItem} from "../model/user/blog/Blog";
import BlogPostItem from "../components/user/blog/BlogPostItem";
import CourseAndPlatformOverview from "../components/user/main-page/CourseAndPlatformOverview";
import FeatureCard from "../components/user/saas/FeatureCard";
import {getProductAccessDeadline} from "../util/ProductAccessUtil";

type LoaderData = {
    articles: Promise<BlogItem[]>;
};

const glassCard = {
    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.3) 0%, rgba(26, 26, 46, 0.5) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: '16px',
};

const courseModules = [
    {
        title: 'Baza',
        description: 'Poznasz wszystkie niezbędne narzędzia konieczne do efektywnego działania. Dowiesz się, jak skutecznie planować, zdobywać wiedzę oraz zarządzać czasem.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/ikony-ked/baza.png',
    },
    {
        title: 'Nastawienie',
        description: 'Zdasz sobie sprawę z wagi odpowiedniego nastawienia. Osiągniesz mindset zwycięzcy i zauważysz, jakie cechy i zachowania blokują Twój rozwój.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/ikony-ked/nastawienie.png',
    },
    {
        title: 'Proces',
        description: 'Proces zakłada stopniowe zdobywanie wiedzy i zagłębianie się w tematykę efektywnego działania. Stosując się do zaleceń i wykonując ćwiczenia szybko zauważysz zdumiewające rezultaty.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/ikony-ked/proces.png',
    },
    {
        title: 'Wsparcie',
        description: 'Dzięki regularnym spotkaniom z twórcą kursu oraz wspierającej społeczności uzyskasz szybką pomoc i zrealizujesz każdy swój cel.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/ikony-ked/mentoring.png',
    },
];

const values = [
    {
        title: 'Dostępność',
        description: 'Uważamy, że dostęp do wysokiej jakości edukacji powinien być zapewniony dla wszystkich, zawsze i wszędzie. Dlatego, by korzystać z Produkacji, potrzebujesz jedynie Internetu.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/dostepnosc.png',
    },
    {
        title: 'Innowacyjność',
        description: 'Wykorzystujemy najnowsze technologie, aby stworzyć najbardziej kompleksowe rozwiązania.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/innowacyjnosc.png',
    },
    {
        title: 'Łatwość korzystania',
        description: 'Tworzymy platformę tak, by była intuicyjna dla każdego. Chcemy by korzystanie z niej było łatwe i przyjemne.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/latwosc+korzystania.png',
    },
    {
        title: 'Profesjonalizm',
        description: 'Dbamy, aby wszelkie materiały były na najwyższym poziomie, a wiedza była przekazywana w przystępny i przejrzysty sposób.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/profesjonalizm.png',
    },
    {
        title: 'Inkluzywność',
        description: 'Produkacja skierowana jest do wszystkich uczniów. Zapewniamy bezpieczną przestrzeń, która zapewni odpowiednie warunki do rozwoju i nauki.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/inkluzywnosc.png',
    },
    {
        title: 'Dialog',
        description: 'Wasza opinia jest dla nas najważniejsza, dlatego słuchamy Waszych uwag, komentarzy i sugestii.',
        image: 'https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/values/dialog.png',
    },
];

export default function Index() {
    const {articles} = useLoaderData() as LoaderData;

    function isPlatformFeaturesVisible() {
        const now = new Date();
        const deadline = getProductAccessDeadline();
        return now >= deadline;
    }

    return <>
        <Meta title='Produkacja OS - Środowisko do nauki, planowania i działania'
              description='Produkacja OS — środowisko do nauki, planowania i działania. Kurs Efektywnego Działania, rozwiązania AI dla uczniów oraz blog.'
              imageUrl='https://produkacja.s3.eu-central-1.amazonaws.com/web/img/logo-500-500-title.png'/>
        <MainDesign containerStyles="pb-28" useGradientBackground={true} addOrb={true}>

            {/* ===== HERO ===== */}
            <div
                className="position-relative overflow-hidden"
                style={{
                    backgroundImage: 'url(https://produkacja.s3.eu-central-1.amazonaws.com/web/main-page/bg/4.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    padding: '20px',
                }}
            >
                {/* Gradient overlay for better text readability */}
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                    background: 'linear-gradient(180deg, rgba(15, 15, 30, 0.3) 0%, rgba(15, 15, 30, 0.1) 40%, rgba(15, 15, 30, 0.5) 100%)',
                    pointerEvents: 'none',
                }}/>

                <div className="position-relative" style={{zIndex: 1, maxWidth: 1100}}>
                    <motion.div
                        initial={{opacity: 0, y: 30}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.7, ease: [0.25, 0.1, 0.25, 1]}}
                    >
                        <h1
                            className='font-heading text-white mb-5'
                            style={{
                                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                                lineHeight: 1.15,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            Pomagamy nastolatkom w pełni wykorzystać swój potencjał.
                        </h1>
                    </motion.div>

                    <motion.p
                        className="text-white mb-6 mx-auto"
                        style={{
                            fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                            lineHeight: 1.7,
                            maxWidth: 650,
                            opacity: 0.85,
                        }}
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 0.85, y: 0}}
                        transition={{duration: 0.7, delay: 0.2}}
                    >
                        Produkacja OS — środowisko wspierane sztuczną inteligencją, które pomoże
                        Ci uczyć się efektywniej i osiągać swoje cele szybciej
                    </motion.p>

                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.6, delay: 0.4}}
                    >
                        <AnimatedLink
                            hashLink={true}
                            className="btn fw-semibold px-5 py-3"
                            style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                color: '#fff',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '14px',
                                backdropFilter: 'blur(10px)',
                                fontSize: '0.95rem',
                            }}
                            onClick={() => {
                                TraceService.addTrace(TraceType.BtnClick, 'btn-1 -> Zacznij działać')
                            }}
                            to='#offerOverview'>
                            Dowiedz się więcej
                            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="ms-2"
                                 style={{verticalAlign: '-2px'}}>
                                <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </AnimatedLink>
                    </motion.div>
                </div>
            </div>

            {/* ===== CZYM JEST PRODUKACJA ===== */}
            <section className="container mt-20 mb-16">
                <motion.div
                    className="text-center mb-10"
                    initial={{opacity: 0, y: 30}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, amount: 0.3}}
                    transition={{duration: 0.6}}
                >
                    <Heading title='Czym jest Produkacja?'
                             h2={true}
                             customStyles='text-center mw-md-4xl mx-auto mb-6'
                             customTitleStyles='font-heading mb-4 fs-4 text-white'/>
                    <p
                        className="mx-auto text-center"
                        style={{
                            color: 'rgba(255, 255, 255, 0.55)',
                            fontSize: 'clamp(0.9rem, 1.5vw, 1.15rem)',
                            lineHeight: 1.8,
                            maxWidth: 700,
                        }}
                    >
                        Uczymy nie tylko jak efektywnie przyswajać wiedzę, ale również jak mistrzowsko zarządzać
                        czasem, uwierzyć w swoje możliwości i skutecznie realizować swoje cele.
                    </p>
                    <p
                        className="mx-auto text-center mt-4"
                        style={{
                            color: 'rgba(255, 255, 255, 0.55)',
                            fontSize: 'clamp(0.9rem, 1.5vw, 1.15rem)',
                            lineHeight: 1.8,
                            maxWidth: 700,
                        }}
                    >
                        Pokazujemy, jak wykorzystując odpowiednie narzędzia,
                        możesz osiągnąć wszystko, czego zapragniesz.
                    </p>
                </motion.div>

                <motion.div
                    className='mw-md-5xl mx-auto text-center'
                    initial={{opacity: 0, y: 40}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, amount: 0.2}}
                    transition={{duration: 0.7, delay: 0.1}}
                >
                    <div className="rounded-4 overflow-hidden" style={{border: '1px solid rgba(255, 255, 255, 0.06)'}}>
                        <img className='img-fluid lazyload'
                             loading='lazy'
                             data-src='https://produkacja.s3.eu-central-1.amazonaws.com/web/o-nas/main-page-2025-05.png'
                             alt='wspólne zdjęcie'/>
                    </div>
                </motion.div>
            </section>

            {/* ===== COURSE & PLATFORM OVERVIEW ===== */}
            <CourseAndPlatformOverview/>

            {/* ===== KURS EFEKTYWNEGO DZIAŁANIA - 4 MODULES ===== */}
            <section className="container mt-10 mb-10">
                <Heading title='Kurs Efektywnego Działania'
                         h2={true}
                         customStyles='text-center mw-md-4xl mx-auto mb-10'
                         customTitleStyles='font-heading mb-4 fs-4 text-white'/>

                <div className='row g-4 mb-10'>
                    <StaggerChildrenWhenVisible>
                        {courseModules.map((mod) => (
                            <motion.div
                                key={mod.title}
                                variants={{
                                    hidden: {opacity: 0, y: 30},
                                    visible: {opacity: 1, y: 0}
                                }}
                                transition={{duration: 0.5, ease: [0.25, 0.1, 0.25, 1]}}
                                className='col-12 col-sm-6 col-lg-3'
                            >
                                <div className="h-100 text-center p-4" style={glassCard}>
                                    <img
                                        className='mb-4'
                                        src={mod.image}
                                        style={{width: 120, height: 120}}
                                        alt={mod.title}
                                    />
                                    <h3 className='fw-semibold text-white mb-3' style={{fontSize: '1.05rem'}}>
                                        {mod.title}
                                    </h3>
                                    <p className='mb-0' style={{
                                        color: 'rgba(255, 255, 255, 0.55)',
                                        fontSize: '0.88rem',
                                        lineHeight: 1.7
                                    }}>
                                        {mod.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </StaggerChildrenWhenVisible>
                </div>

                <div className="text-center">
                    <AnimatedLink
                        className="btn fw-semibold px-5 py-3"
                        style={{
                            background: 'linear-gradient(135deg, #2ad167 0%, #22b859 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '14px',
                            fontSize: '0.95rem',
                        }}
                        onClick={() => {
                            TraceService.addTrace(TraceType.BtnClick, 'btn-2 -> Zapisz się teraz')
                        }}
                        to={'/oferta/kurs-efektywnego-dzialania'}>
                        Zapisz się teraz
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="ms-2"
                             style={{verticalAlign: '-2px'}}>
                            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </AnimatedLink>
                </div>
            </section>

            {/* ===== PLATFORM FEATURES (conditional) ===== */}
            {isPlatformFeaturesVisible() && (
                <motion.section
                    className="container py-10 my-5"
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true, amount: 0.3}}
                    transition={{duration: 0.6}}
                >
                    <Heading title='Produkacja OS'
                             h2={true}
                             customStyles='mb-8 text-center mw-md-4xl mx-auto'
                             customTitleStyles='font-heading mb-4 fs-4 text-white'/>

                    <motion.div
                        className="row g-4"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.3}}
                        variants={{
                            hidden: {opacity: 0},
                            visible: {opacity: 1, transition: {staggerChildren: 0.15}}
                        }}
                    >
                        <div className="col-md-6 col-lg-3">
                            <FeatureCard
                                title="Efektywna nauka"
                                description="Nasze algorytmy dostosowują się do Twojego stylu nauki, aby zmaksymalizować przyswajanie wiedzy i zminimalizować czas nauki."
                                icon="bi-lightbulb-fill"
                                iconColorClass="text-success"
                            />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <FeatureCard
                                title="Inteligentny planer"
                                description="Zaplanuj swoje sesje nauki optymalnie dzięki naszemu inteligentnemu planerowi, który dopasowuje się do Twojego stylu życia."
                                icon="bi-calendar-check"
                                iconColorClass="text-success"
                            />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <FeatureCard
                                title="System fiszek"
                                description="Wzmocnij swoją pamięć dzięki naszemu zaawansowanemu systemowi fiszek z powtórkami w odpowiednich odstępach czasu."
                                icon="bi-card-list"
                                iconColorClass="text-warning"
                            />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <FeatureCard
                                title="Wsparcie AI 24/7"
                                description="Nasza sztuczna inteligencja analizuje Twoje postępy i dostarcza spersonalizowanych rekomendacji. Możesz pytać o cokolwiek i kiedykolwiek!"
                                icon="bi-robot"
                                iconColorClass="text-warning"
                            />
                        </div>
                    </motion.div>
                </motion.section>
            )}

            {/* ===== VALUES ===== */}
            <section className="container mt-16 mb-16">
                <Heading title='Co jest dla nas ważne?'
                         h2={true}
                         customStyles='mb-10 text-center mw-md-4xl mx-auto'
                         customTitleStyles='font-heading mb-4 fs-4 text-white'/>

                <div className='row align-items-center g-8'>
                    {/* Left values */}
                    <div className='col-12 col-lg-4'>
                        <div className='d-flex flex-column gap-5'>
                            {values.slice(0, 3).map((val) => (
                                <SlideInWhenVisible key={val.title} initialX={-80}>
                                    <div className="text-center p-4" style={glassCard}>
                                        <div
                                            className='mb-3 d-flex justify-content-center align-items-center mx-auto rounded-3'
                                            style={{
                                                width: 64,
                                                height: 64,
                                                background: 'rgba(42, 209, 103, 0.4)',
                                                border: '1.5px solid rgba(42, 209, 103, 0.5)',
                                                boxShadow: '0 0 16px rgba(42, 209, 103, 0.15)',
                                            }}
                                        >
                                            <img src={val.image} alt={val.title}
                                                 style={{
                                                     width: 36,
                                                     height: 36,
                                                     filter: 'brightness(1.8) saturate(1.4)'
                                                 }}/>
                                        </div>
                                        <h3 className='fw-semibold text-white mb-2' style={{fontSize: '1rem'}}>
                                            {val.title}
                                        </h3>
                                        <p className='mb-0' style={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            fontSize: '0.85rem',
                                            lineHeight: 1.7
                                        }}>
                                            {val.description}
                                        </p>
                                    </div>
                                </SlideInWhenVisible>
                            ))}
                        </div>
                    </div>

                    {/* Center image */}
                    <div className='col-12 col-lg-4'>
                        <motion.div
                            className='text-center'
                            initial={{opacity: 0, scale: 0.95}}
                            whileInView={{opacity: 1, scale: 1}}
                            viewport={{once: true}}
                            transition={{duration: 0.7}}
                        >
                            <img
                                className='img-fluid rounded-4'
                                style={{maxHeight: 650, border: '1px solid rgba(255, 255, 255, 0.06)'}}
                                src='https://produkacja.s3.eu-central-1.amazonaws.com/web/o-nas/Produkacja+small-19.jpg'
                                alt='zdjecie'
                                loading='lazy'
                            />
                        </motion.div>
                    </div>

                    {/* Right values */}
                    <div className='col-12 col-lg-4'>
                        <div className='d-flex flex-column gap-5'>
                            {values.slice(3, 6).map((val) => (
                                <SlideInWhenVisible key={val.title} initialX={80}>
                                    <div className="text-center p-4" style={glassCard}>
                                        <div
                                            className='mb-3 d-flex justify-content-center align-items-center mx-auto rounded-3'
                                            style={{
                                                width: 64,
                                                height: 64,
                                                background: 'rgba(42, 209, 103, 0.4)',
                                                border: '1.5px solid rgba(42, 209, 103, 0.5)',
                                                boxShadow: '0 0 16px rgba(42, 209, 103, 0.15)',
                                            }}
                                        >
                                            <img src={val.image} alt={val.title}
                                                 style={{
                                                     width: 36,
                                                     height: 36,
                                                     filter: 'brightness(1.8) saturate(1.4)'
                                                 }}/>
                                        </div>
                                        <h3 className='fw-semibold text-white mb-2' style={{fontSize: '1rem'}}>
                                            {val.title}
                                        </h3>
                                        <p className='mb-0' style={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            fontSize: '0.85rem',
                                            lineHeight: 1.7
                                        }}>
                                            {val.description}
                                        </p>
                                    </div>
                                </SlideInWhenVisible>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== BLOG ===== */}
            <section className='mx-auto mw-md-5xl container mt-10'>
                <Heading title='Skorzystaj z bezpłatnej wiedzy'
                         subtitle='Na naszym blogu oraz mediach społecznościowych znajdziesz wiele materiałów,
                         w których dzielimy się wiedzą i doświadczeniami.'
                         customStyles='text-center mw-md-5xl mx-auto mb-10'
                         customTitleStyles='font-heading mb-4 fs-4 text-white'
                         customSubtitleStyles='fs-10 fw-medium text-secondary-light mw-md-4xl mx-auto'/>
                <Suspense fallback={<LoadingPage styles="pt-10" useGradientBackground={false}/>}>
                    <Await resolve={articles} errorElement={<></>}>
                        {(loaderArticles: BlogItem[]) => <>
                            <div className='mb-12 row g-8'>
                                {
                                    loaderArticles.map((item, index) => (
                                        <BlogPostItem key={item.id} blogItem={item} isAdmin={false}
                                                      index={index}
                                                      largeCard={true}
                                                      setArticleToDelete={() => {
                                                      }}
                                                      setConfirmDeleteModalOpen={() => {
                                                      }}/>
                                    ))
                                }
                            </div>
                        </>}
                    </Await>
                </Suspense>
                <div className="text-center pb-8">
                    <AnimatedLink
                        className="btn fw-semibold px-5 py-3"
                        style={{
                            background: 'linear-gradient(135deg, #2ad167 0%, #22b859 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '14px',
                            fontSize: '0.95rem',
                        }}
                        onClick={() => {
                            TraceService.addTrace(TraceType.BtnClick, 'btn3 -> zobacz więcej blog')
                        }}
                        to={'/blog'}>
                        Zobacz więcej
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="ms-2"
                             style={{verticalAlign: '-2px'}}>
                            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </AnimatedLink>
                </div>
            </section>
        </MainDesign>
    </>
}

export async function loader() {
    return defer({
        articles: BlogService.getRandomArticles(2)
    });
}
